import { gql } from "@apollo/client";

import type { AccountType } from "../utils";

/**
 * Domain types for the Customer Provisioning (CNC) GraphQL API.
 *
 * These are hand-written to match the queries/mutations in this file (no codegen).
 * They are exported so that the other CustomerProvisioning files converted in
 * parallel/subsequent waves can import the shared shapes instead of redefining them.
 */

// Generic CNC mutation result. Most CNC mutations return { ok, msg }; a few omit `ok`.
export interface CncMutationResult {
  ok: boolean;
  msg: string;
}

export interface CncMessageOnlyResult {
  msg: string;
}

// --- cnc_database_instances ---

export interface CncGetDatabaseInstancesData {
  cnc_database_instances: string[];
}

// --- cnc_organizations ---

export interface CncAccount {
  id: string;
  name: string;
  enabled: boolean;
  database_identifier: string;
  database_schema: string;
  account_type: AccountType;
}

export interface CncOrganization {
  slug: string;
  name: string;
  domains: string[] | null;
  identity_provider: string | null;
  password_enabled: boolean;
  primary_domain: string;
  enabled: boolean;
  accounts: CncAccount[];
}

export interface CncGetOrganizationsData {
  cnc_organizations: CncOrganization[];
}

// --- cnc_users ---

export interface CncIdName {
  id: string;
  name: string;
}

export interface CncRelayEdges<TNode> {
  edges: { node: TNode }[];
}

export interface CncProfileNode extends CncIdName {
  scopes: CncRelayEdges<CncIdName>;
  scope_groups: CncRelayEdges<CncIdName>;
}

export interface CncSamlGroupProfile {
  saml_group: string;
  profile: CncIdName;
}

export interface CncUserNode {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  last_login: string | null;
  visible: boolean;
  saml_group_profiles: CncSamlGroupProfile[] | null;
  profiles: CncRelayEdges<CncProfileNode>;
  role: CncIdName | null;
  // Read by ManageDomainUsers; not selected by CNC_GET_USERS, so resolves to
  // undefined at runtime. Optional to keep that access type-safe.
  identity_provider_source?: string | null;
}

export interface CncGetUsersData {
  cnc_users: CncRelayEdges<CncUserNode>;
}

export interface CncGetUsersVariables {
  account_uuid: string;
}

// --- cnc_invite_user ---

export interface UserInput {
  first_name: string;
  last_name: string;
  email: string;
  visible?: boolean;
  role_id?: string;
  profile_ids?: string[];
  password?: string;
}

export interface CncInviteUserVariables {
  send_email?: boolean;
  user: UserInput;
  account_uuid: string;
}

export interface CncInviteUserData {
  cnc_invite_user: CncMutationResult;
}

// --- cnc_new_organization ---

export interface NewOrganizationVariables {
  slug: string;
  name: string;
  primary_domain: string;
}

export interface NewOrganizationData {
  cnc_new_organization: CncMutationResult;
}

// --- cnc_delete_organization ---

export interface DeleteOrganizationVariables {
  organization_slug: string;
}

export interface DeleteOrganizationData {
  cnc_delete_organization: CncMutationResult;
}

// --- cnc_new_account ---

export interface NewAccountInput {
  name: string;
  database_identifier?: string;
  database_schema?: string;
  account_type?: AccountType;
}

export interface NewAccountVariables {
  organization_slug: string;
  new_account_input: NewAccountInput;
}

export interface NewAccountData {
  cnc_new_account: CncMutationResult;
}

// --- cnc_delete_account / cnc_destroy_account ---

export interface AccountUuidVariables {
  account_uuid: string;
}

export interface DeleteAccountData {
  cnc_delete_account: CncMutationResult;
}

export interface DestroyAccountData {
  cnc_destroy_account: CncMutationResult;
}

// --- cnc_add_domain / cnc_delete_domain ---

export interface DomainVariables {
  domain: string;
  organization_slug: string;
}

export interface CreateNewDomainData {
  cnc_add_domain: CncMessageOnlyResult;
}

export interface DeleteDomainData {
  cnc_delete_domain: CncMessageOnlyResult;
}

// --- cnc_delete_user ---

export interface CncDeleteUserVariables {
  user_id: string;
  account_uuid: string;
}

export interface CncDeleteUserData {
  cnc_delete_user: CncMutationResult;
}

// --- cnc_change_user_fields (edit user) ---

export interface CncEditUserVariables {
  account_uuid: string;
  user_id: string;
  first_name: string;
  last_name: string;
  role_id?: string;
  profile_ids?: (string | null)[];
  visible?: boolean;
}

export interface CncEditUserData {
  cnc_change_user_fields: CncMutationResult;
}

// --- cnc_reset_user_password ---

export interface ResetUserPasswordVariables {
  account_uuid: string;
  user_id: string;
}

export interface ResetUserPasswordData {
  cnc_reset_user_password: CncMutationResult;
}

// --- set_user_password (change password) ---

export interface ChangeUserPasswordVariables {
  account_uuid: string;
  user_id: string;
  new_password: string;
}

export interface ChangeUserPasswordData {
  set_user_password: CncMutationResult;
}

// --- cnc_datasources ---

export interface CncDatasourceDefinition {
  name: string;
  enable_check_connection: boolean;
}

export interface CncDatasourceNode {
  id: string;
  friendly_name: string;
  datasource_definition: CncDatasourceDefinition;
}

export interface CncGetDatasourcesData {
  cnc_datasources: CncRelayEdges<CncDatasourceNode>;
}

export interface CncGetDatasourcesVariables {
  account_uuid: string;
}

// --- cnc_check_datasource_connection ---

export interface CncCheckDatasourceConnectionVariables {
  account_uuid: string;
  datasource_id: string;
}

export interface CncCheckDatasourceConnectionData {
  cnc_check_datasource_connection: {
    msg: string;
    task_id: string;
  };
}

// --- cnc_launch_collection_action ---

export interface CncLaunchCollectionActionVariables {
  account_uuid: string;
  subject_id: string;
}

export interface CncLaunchCollectionActionData {
  cnc_launch_collection_action: {
    msg: string;
    launched_tasks: string[];
  };
}

// --- cnc_trigger_delete_datasource_data ---

export interface CncTriggerDeleteDatasourceDataVariables {
  account_uuid?: string;
  datasource_id: string;
  dry_run?: boolean;
  delete_resources?: boolean;
  reference_date?: string;
  confirm_account_uuid?: string;
  confirm_datasource_id?: string;
  confirm_phrase?: string;
}

export interface CncTriggerDeleteDatasourceDataData {
  cnc_trigger_delete_datasource_data: {
    op_id: string | null;
  } | null;
}

// --- cnc_deletion_operation_status ---

export interface CncDeletionOperationEvent {
  component: string;
  step: string;
  status: string;
  item_count: number | null;
  reference_id: string;
  inserted_at: string;
}

export interface CncDeletionOperationStatus {
  op_id: string;
  overall_status: string;
  events: CncDeletionOperationEvent[];
}

export interface CncDeletionOperationStatusVariables {
  account_uuid?: string;
  op_id: string;
}

export interface CncDeletionOperationStatusData {
  cnc_deletion_operation_status?: CncDeletionOperationStatus;
}

// --- cnc_disconnect_saml ---

export interface CncDisconnectSamlVariables {
  organization_slug: string;
}

export interface CncDisconnectSamlData {
  cnc_disconnect_saml: CncMutationResult;
}

export const CNC_GET_DATABASE_INSTANCES = gql`
  query cnc_database_instances {
    cnc_database_instances
  }
`;

export const CNC_GET_ORGANIZATIONS = gql`
  query cnc_organizations {
    cnc_organizations {
      slug
      name
      domains
      identity_provider
      password_enabled
      primary_domain
      enabled
      accounts {
        id
        name
        enabled
        database_identifier
        database_schema
        account_type
      }
    }
  }
`;

export const CNC_GET_USERS = gql`
  query cnc_users($account_uuid: String!) {
    cnc_users(
      account_uuid: $account_uuid
      filters: { not: { activity_status: DELETED } }
    ) {
      edges {
        node {
          id
          email
          first_name
          last_name
          last_login
          visible
          saml_group_profiles {
            saml_group
            profile {
              id
              name
            }
          }
          profiles {
            edges {
              node {
                id
                name
                scopes {
                  edges {
                    node {
                      id
                      name
                    }
                  }
                }
                scope_groups {
                  edges {
                    node {
                      id
                      name
                    }
                  }
                }
              }
            }
          }
          role {
            id
            name
          }
        }
      }
    }
  }
`;

export const CNC_INVITE_USER = gql`
  mutation cncInviteUser(
    $send_email: Boolean
    $user: UserInput!
    $account_uuid: String!
  ) {
    cnc_invite_user(
      send_email: $send_email
      user: $user
      account_uuid: $account_uuid
    ) {
      ok
      msg
    }
  }
`;

export const NEW_ORGANIZATION = gql`
  mutation ($slug: String!, $name: String!, $primary_domain: String!) {
    cnc_new_organization(
      slug: $slug
      name: $name
      primary_domain: $primary_domain
    ) {
      ok
      msg
    }
  }
`;

export const DELETE_ORGANIZATION = gql`
  mutation ($organization_slug: String!) {
    cnc_delete_organization(organization_slug: $organization_slug) {
      ok
      msg
    }
  }
`;

export const NEW_ACCOUNT = gql`
  mutation ($organization_slug: String!, $new_account_input: NewAccountInput!) {
    cnc_new_account(
      organization_slug: $organization_slug
      new_account_input: $new_account_input
    ) {
      ok
      msg
    }
  }
`;

// Note: not renamed for backward compatibility — this mutation disables the account (it does not hard-delete it).
export const DELETE_ACCOUNT = gql`
  mutation ($account_uuid: String!) {
    cnc_delete_account(account_uuid: $account_uuid) {
      ok
      msg
    }
  }
`;

export const DESTROY_ACCOUNT = gql`
  mutation ($account_uuid: String!) {
    cnc_destroy_account(account_uuid: $account_uuid) {
      ok
      msg
    }
  }
`;

export const CREATE_NEW_DOMAIN = gql`
  mutation cnc_add_domain($domain: String!, $organization_slug: String!) {
    cnc_add_domain(domain: $domain, organization_slug: $organization_slug) {
      msg
    }
  }
`;

export const DELETE_DOMAIN = gql`
  mutation cnc_delete_domain($domain: String!, $organization_slug: String!) {
    cnc_delete_domain(domain: $domain, organization_slug: $organization_slug) {
      msg
    }
  }
`;

export const CNC_DELETE_USER = gql`
  mutation cncDeleteUser($user_id: String!, $account_uuid: String!) {
    cnc_delete_user(user_id: $user_id, account_uuid: $account_uuid) {
      ok
      msg
    }
  }
`;

export const CNC_EDIT_USER = gql`
  mutation cncEditUser(
    $account_uuid: String!
    $user_id: String!
    $first_name: String!
    $last_name: String!
    $role_id: String
    $profile_ids: [String]
    $visible: Boolean
  ) {
    cnc_change_user_fields(
      user_id: $user_id
      first_name: $first_name
      last_name: $last_name
      role_id: $role_id
      profile_ids: $profile_ids
      account_uuid: $account_uuid
      visible: $visible
    ) {
      ok
      msg
    }
  }
`;

export const RESET_USER_PASSWORD = gql`
  mutation cnc_reset_user_password($account_uuid: String!, $user_id: String!) {
    cnc_reset_user_password(account_uuid: $account_uuid, user_id: $user_id) {
      ok
      msg
    }
  }
`;

export const CHANGE_USER_PASSWORD = gql`
  mutation change_user_password(
    $account_uuid: String!
    $user_id: String!
    $new_password: String!
  ) {
    set_user_password(
      account_uuid: $account_uuid
      user_id: $user_id
      new_password: $new_password
    ) {
      ok
      msg
    }
  }
`;

export const CNC_GET_DATASOURCES = gql`
  query cnc_datasources($account_uuid: String!) {
    cnc_datasources(account_uuid: $account_uuid) {
      edges {
        node {
          id
          friendly_name
          datasource_definition {
            name
            enable_check_connection
          }
        }
      }
    }
  }
`;

export const CNC_CHECK_DATASOURCE_CONNECTION = gql`
  mutation cnc_check_datasource_connection(
    $account_uuid: String!
    $datasource_id: String!
  ) {
    cnc_check_datasource_connection(
      account_uuid: $account_uuid
      datasource_id: $datasource_id
    ) {
      msg
      task_id
    }
  }
`;

export const CNC_LAUNCH_COLLECTION_ACTION = gql`
  mutation cnc_launch_collection_action(
    $account_uuid: String!
    $subject_id: String!
  ) {
    cnc_launch_collection_action(
      account_uuid: $account_uuid
      subject_id_: $subject_id
      launch_sync: true
    ) {
      msg
      launched_tasks
    }
  }
`;

export const CNC_TRIGGER_DELETE_DATASOURCE_DATA = gql`
  mutation cnc_trigger_delete_datasource_data(
    $account_uuid: String
    $datasource_id: String!
    $dry_run: Boolean
    $delete_resources: Boolean
    $reference_date: String
    $confirm_account_uuid: String
    $confirm_datasource_id: String
    $confirm_phrase: String
  ) {
    cnc_trigger_delete_datasource_data(
      account_uuid: $account_uuid
      datasource_id: $datasource_id
      dry_run: $dry_run
      delete_resources: $delete_resources
      reference_date: $reference_date
      confirm_account_uuid: $confirm_account_uuid
      confirm_datasource_id: $confirm_datasource_id
      confirm_phrase: $confirm_phrase
    ) {
      op_id
    }
  }
`;

export const CNC_DELETION_OPERATION_STATUS = gql`
  query cnc_deletion_operation_status($account_uuid: String, $op_id: String!) {
    cnc_deletion_operation_status(account_uuid: $account_uuid, op_id: $op_id) {
      op_id
      overall_status
      events {
        component
        step
        status
        item_count
        reference_id
        inserted_at
      }
    }
  }
`;

export const CNC_DISCONNECT_SAML = gql`
  mutation cnc_disconnect_saml($organization_slug: String!) {
    cnc_disconnect_saml(organization_slug: $organization_slug) {
      ok
      msg
    }
  }
`;
